import React, { useCallback, useEffect, useState } from 'react';
import { Auth } from '~/amplify';
import { PHONE_NUMBER_ATTRIBUTE_KEY } from '~/components/page/Auth/constants';
import AnimatedBlock from '~/components/atom/AnimatedBlock';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { Body, Heading3, Heading4 } from '~/components/atom/Typography';
import { reporter } from '~/hooks/useErrorReporter';
import useAddToast from '~/hooks/useAddToast';
import formatToastMessage from '~/util/formatToastMessage';
import StatusIndicator from '~/components/atom/StatusIndicator';
import CallToActionBlock, {
  type Props as CallToActionBlockProps,
} from '~/components/organism/CallToActionBlock';
import Button from '~/components/atom/Button';
import {
  ProfileCognitoPhoneProvider,
  useProfileCognitoPhone,
} from '../../context/ProfileCognitoPhoneContext';
import Validation from '~/util/Validation';
import MFASetupTotpModal from './components/MFASetupTotpModal/index';
import MFASetupPhoneModal from './components/MFASetupPhoneModal/index';
import MFAVerifyPhoneModal from './components/MFAVerifyPhoneModal/index';
import StatusLabel from './components/StatusLabel';
import MFADeleteTotpModal from './components/MFADeleteTotpModal/index';
import text from './text';
import BndlLoading from '~/components/atom/BndlLoading';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import useScrollToHashElement from '~/hooks/useScrollToHashElement';
import useSessionHydration from '~/hooks/useSessionHydration';
import useUpdateMFAPreferenceAndSyncUser from '~/amplify/utils/updateMFAPreferenceAndSyncUser';
import enableSmsMfaPreference, {
  TotpRequiredForSmsError,
} from '~/amplify/utils/enableSmsMfaPreference';

export const MFA_SETTINGS_BLOCK_ID = 'mfa-settings';

type MFAPreference = {
  totpEnabled: boolean;
  smsEnabled: boolean;
  maskedPhone: string | null;
  phoneNumberVerified: boolean;
};

enum TotpIndicatorStatus {
  Active = 'active',
  Inactive = 'inactive',
}

enum MfaRowStatus {
  Active = 'active',
  Warning = 'warning',
  Inactive = 'inactive',
}

const getTotpIndicatorStatus = (mfa: MFAPreference): TotpIndicatorStatus =>
  mfa.totpEnabled ? TotpIndicatorStatus.Active : TotpIndicatorStatus.Inactive;

const getRecoveryPhoneRowStatus = (mfa: MFAPreference): MfaRowStatus => {
  if (!mfa.maskedPhone) return MfaRowStatus.Inactive;
  if (mfa.phoneNumberVerified) return MfaRowStatus.Active;
  return MfaRowStatus.Warning;
};

const getSmsRowStatus = (mfa: MFAPreference): MfaRowStatus => {
  if (!mfa.phoneNumberVerified) return MfaRowStatus.Inactive;
  if (mfa.smsEnabled) return MfaRowStatus.Active;
  return MfaRowStatus.Warning;
};

const getOverallMfaIndicatorStatus = (
  totp: TotpIndicatorStatus,
  sms: MfaRowStatus,
): MfaRowStatus => {
  if (totp !== TotpIndicatorStatus.Active) return MfaRowStatus.Inactive;
  if (sms === MfaRowStatus.Active) return MfaRowStatus.Active;
  return MfaRowStatus.Warning;
};

type Props = {
  /**
   * Whether the account requires MFA. If true, the delete button is disabled.
   */
  mfaRequired: boolean | null | undefined;
};

enum MfaSettingsModalType {
  SetupTotp = 'setupTotp',
  SetupPhone = 'setupPhone',
  VerifyPhone = 'verifyPhone',
}

type MfaCallToActionConfig = {
  id: string;
  show: boolean;
  icon: NonNullable<CallToActionBlockProps['icon']>;
  header: string;
  description: string;
  button: NonNullable<CallToActionBlockProps['button']>;
};

export const MFASettingsBlockInner: React.FCC<Props> = ({ mfaRequired }) => {
  const {
    phoneNumber,
    phoneVerified,
    loading: cognitoPhoneAttrsLoading,
    refresh: refreshCognitoPhone,
  } = useProfileCognitoPhone();
  const [loading, setLoading] = useState(cognitoPhoneAttrsLoading);

  useScrollToHashElement({
    elementId: MFA_SETTINGS_BLOCK_ID,
    enabled: !loading,
  });

  const [mfa, setMfa] = useState<MFAPreference>({
    totpEnabled: false,
    smsEnabled: false,
    maskedPhone: null,
    phoneNumberVerified: false,
  });

  const [deletingTotp, setDeletingTotp] = useState(false);
  const [showDeleteTotpModal, setShowDeleteTotpModal] = useState(false);
  const [modalType, setModalType] = useState<MfaSettingsModalType | null>(null);

  const addToast = useAddToast();
  const { id: accountId } = useCurrentAccount();
  const { updateMFAPreferenceWithSync } = useUpdateMFAPreferenceAndSyncUser();

  const loadMFAPreferences = useCallback(async () => {
    setLoading(true);
    try {
      const preferences = await Auth.fetchMFAPreference();

      setMfa(prev => ({
        ...prev,
        totpEnabled: preferences.enabled?.includes('TOTP') ?? false,
        smsEnabled: preferences.enabled?.includes('SMS') ?? false,
      }));
    } catch (err) {
      reporter.captureMessage(
        `MFASettingsBlock: Failed to load MFA preferences: ${err}`,
        'error',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadMFAPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeMfaModal = () => {
    setModalType(null);
  };

  const onSmsEnabled = () => {
    setMfa(prev => ({ ...prev, smsEnabled: true }));
    void loadMFAPreferences();
  };

  const continueToPhoneAfterTotp = () => {
    setModalType(MfaSettingsModalType.SetupPhone);
  };

  const goToTotpSetupFromPhoneModal = () => {
    setModalType(MfaSettingsModalType.SetupTotp);
  };

  useEffect(() => {
    setMfa(prev => ({
      ...prev,
      maskedPhone: phoneNumber ? Validation.Phone.maskPhone(phoneNumber) : null,
      phoneNumberVerified: phoneVerified,
    }));
  }, [phoneNumber, phoneVerified]);

  const onDisableMFA = async () => {
    setDeletingTotp(true);
    try {
      await updateMFAPreferenceWithSync({
        accountId,
        update: { totp: 'DISABLED', sms: 'DISABLED' },
      });

      await Auth.deleteUserAttributes({
        userAttributeKeys: [PHONE_NUMBER_ATTRIBUTE_KEY],
      });
      setMfa(prev => ({ ...prev, totpEnabled: false, smsEnabled: false }));
      await refreshCognitoPhone();
      addToast([formatToastMessage(text.deleteSuccessMessage, 'success')]);
      setShowDeleteTotpModal(false);
    } catch (err) {
      reporter.captureMessage(
        `MFASettingsBlock: Failed to disable TOTP: ${err}`,
        'error',
      );
      addToast([formatToastMessage(text.disableMfaErrorMessage, 'danger')]);
      await refreshCognitoPhone();
    } finally {
      setDeletingTotp(false);
    }
  };

  const onEnableSms = async () => {
    try {
      await enableSmsMfaPreference();
      setMfa(prev => ({ ...prev, smsEnabled: true }));
      addToast([formatToastMessage(text.enableSmsSuccess, 'success')]);
    } catch (err) {
      reporter.captureMessage(
        `MFASettingsBlock: Failed to enable SMS: ${err}`,
        'error',
      );
      const message =
        err instanceof TotpRequiredForSmsError
          ? text.enableSmsRequiresTotp
          : text.enableSmsErrorMessage;
      addToast([formatToastMessage(message, 'danger')]);
    }
  };

  const totpStatus = getTotpIndicatorStatus(mfa);
  const phoneStatus = getRecoveryPhoneRowStatus(mfa);
  const smsStatus = getSmsRowStatus(mfa);
  const overallStatus = getOverallMfaIndicatorStatus(totpStatus, smsStatus);

  const showCtaSetupTotp = !mfa.totpEnabled;
  const showCtaAddPhone =
    mfa.totpEnabled && phoneStatus === MfaRowStatus.Inactive;
  const showCtaEnableSms =
    mfa.totpEnabled &&
    phoneStatus === MfaRowStatus.Active &&
    smsStatus === MfaRowStatus.Warning;
  const showCtaVerifyPhoneNumber =
    mfa.totpEnabled && phoneStatus === MfaRowStatus.Warning;

  const mfaCallToActions: Array<MfaCallToActionConfig> = [
    {
      id: 'mfa-cta-setup-totp',
      show: showCtaSetupTotp,
      icon: { name: 'exclamation', background: { group: 'warning' } },
      header: text.ctaSetupTotp,
      description: text.ctaSetupTotpDescription,
      button: {
        icon: 'arrowRight',
        label: text.addDevice,
        appearance: 'primary',
        onClick: () => setModalType(MfaSettingsModalType.SetupTotp),
      },
    },
    {
      id: 'mfa-cta-add-phone',
      show: showCtaAddPhone,
      icon: { name: 'exclamation', background: { group: 'warning' } },
      header: text.ctaAddPhone,
      description: text.ctaAddPhoneDescription,
      button: {
        icon: 'arrowRight',
        label: text.addPhone,
        appearance: 'primary',
        onClick: () => setModalType(MfaSettingsModalType.SetupPhone),
      },
    },
    {
      id: 'mfa-cta-enable-sms',
      show: showCtaEnableSms,
      icon: { name: 'info', background: { group: 'info' } },
      header: text.ctaEnableSms,
      description: text.ctaEnableSmsDescription,
      button: {
        icon: 'check',
        label: text.enableSms,
        appearance: 'primary',
        onClick: onEnableSms,
      },
    },
    {
      id: 'mfa-cta-verify-phone',
      show: showCtaVerifyPhoneNumber,
      icon: { name: 'triangle', background: { group: 'warning' } },
      header: text.ctaVerifyPhoneNumber,
      description: text.ctaVerifyPhoneNumberDescription,
      button: {
        icon: 'arrowRight',
        label: text.verifyPhone,
        appearance: 'primary',
        onClick: () => setModalType(MfaSettingsModalType.VerifyPhone),
      },
    },
  ];

  if (loading) {
    return (
      <AnimatedBlock>
        <JustificationContainer direction="column" gap="s">
          <Heading3 size="m" margin={[null]}>
            {text.header}
          </Heading3>
          <BndlLoading />
        </JustificationContainer>
      </AnimatedBlock>
    );
  }

  return (
    <AnimatedBlock id={MFA_SETTINGS_BLOCK_ID}>
      <JustificationContainer
        align="center"
        justification="space-between"
        margin={[null, null, 'xxs', null]}
      >
        <JustificationContainer align="center" gap="s">
          <Heading3 size="m" margin={[null]}>
            {text.header}
          </Heading3>

          <StatusIndicator status={overallStatus} />
        </JustificationContainer>
        {mfa.totpEnabled && !mfaRequired && (
          <Button
            appearance="danger"
            ghost
            label={text.disableMfa}
            loading={deletingTotp}
            onClick={() => setShowDeleteTotpModal(true)}
          />
        )}
      </JustificationContainer>

      {mfaCallToActions
        .filter(cta => cta.show)
        .map(({ id, show: _show, ...ctaProps }) => (
          <CallToActionBlock key={id} {...ctaProps} margin={['base', null]} />
        ))}

      {/* Preferred method — TOTP */}
      <Heading4 size="m" margin={['base', null, 'xxs', null]}>
        {text.primaryVerificationMethod}
      </Heading4>
      <JustificationContainer
        align="center"
        justification="space-between"
        gap="base"
        margin={[null, null, 'm', null]}
      >
        <JustificationContainer align="center" gap="s" wrap="wrap">
          <Body size="base" margin={[null]}>
            {text.authenticatorApp}
          </Body>
          <StatusLabel
            status={totpStatus}
            label={
              totpStatus === TotpIndicatorStatus.Active
                ? text.active
                : text.notConfigured
            }
          />
        </JustificationContainer>
      </JustificationContainer>

      {/* Backup method — Phone + SMS */}
      <Heading4 size="m" margin={['base', null, 'xxs', null]}>
        {text.backupMethodHeader}
      </Heading4>

      {/* Phone number row */}
      <JustificationContainer
        align="center"
        justification="space-between"
        gap="base"
        margin={[null, null, 's', null]}
      >
        <JustificationContainer align="center" gap="s" wrap="wrap">
          <Body size="base" margin={[null]}>
            {text.phoneNumberLabel}
            {mfa.maskedPhone ? ` ${mfa.maskedPhone}` : ''}
          </Body>
          <StatusLabel
            status={phoneStatus}
            label={
              phoneStatus === MfaRowStatus.Active
                ? text.verified
                : phoneStatus === MfaRowStatus.Warning
                  ? text.notVerified
                  : text.notConfigured
            }
          />
        </JustificationContainer>

        <JustificationContainer align="center" gap="xxs">
          {mfa.totpEnabled && phoneStatus === MfaRowStatus.Inactive && (
            <Button
              label={text.addPhone}
              appearance="secondary"
              icon="plus"
              ghost
              size="small"
              disabled={!mfa.totpEnabled}
              onClick={() => setModalType(MfaSettingsModalType.SetupPhone)}
            />
          )}

          {(phoneStatus === MfaRowStatus.Warning ||
            phoneStatus === MfaRowStatus.Active) && (
            <Button
              aria-label={text.ariaPhoneSettings}
              onClick={() => setModalType(MfaSettingsModalType.SetupPhone)}
              icon="gear"
              ghost
              appearance="tertiary"
              size="small"
            />
          )}
        </JustificationContainer>
      </JustificationContainer>

      {/* SMS verification row */}
      {smsStatus !== MfaRowStatus.Inactive && (
        <JustificationContainer
          align="center"
          justification="space-between"
          gap="base"
        >
          <JustificationContainer align="center" gap="s" wrap="wrap">
            <Body size="base" margin={[null]}>
              {text.smsVerificationLabel}
            </Body>
            <StatusLabel
              status={smsStatus}
              label={
                smsStatus === MfaRowStatus.Active
                  ? text.active
                  : smsStatus === MfaRowStatus.Warning
                    ? text.notActivated
                    : text.notAvailable
              }
            />
          </JustificationContainer>
        </JustificationContainer>
      )}

      {showDeleteTotpModal && (
        <MFADeleteTotpModal
          onClose={() => setShowDeleteTotpModal(false)}
          onConfirm={onDisableMFA}
          deleting={deletingTotp}
          hasSmsEnabled={mfa.smsEnabled}
        />
      )}

      {modalType === MfaSettingsModalType.SetupTotp && (
        <MFASetupTotpModal
          onClose={closeMfaModal}
          onContinueToPhoneSetup={continueToPhoneAfterTotp}
          onRefreshMFAPreferences={loadMFAPreferences}
        />
      )}
      {modalType === MfaSettingsModalType.SetupPhone && (
        <MFASetupPhoneModal
          onClose={closeMfaModal}
          alreadyEnabled={phoneStatus === MfaRowStatus.Active}
          onSmsEnabled={onSmsEnabled}
          totpEnabled={mfa.totpEnabled}
          onGoToTotpSetup={goToTotpSetupFromPhoneModal}
        />
      )}
      {modalType === MfaSettingsModalType.VerifyPhone && (
        <MFAVerifyPhoneModal
          onClose={closeMfaModal}
          onSmsEnabled={onSmsEnabled}
          totpEnabled={mfa.totpEnabled}
          onGoToTotpSetup={goToTotpSetupFromPhoneModal}
        />
      )}
    </AnimatedBlock>
  );
};

const MFASettingsBlock: React.FCC<Props> = props => {
  const [{ impersonatedBy, me: currentUser }] = useSessionHydration();

  const hasMfa = currentUser.hasMfa;

  if (impersonatedBy) {
    return (
      <AnimatedBlock>
        <JustificationContainer direction="column" gap="s">
          <Heading3 size="m" margin={[null]}>
            {text.header}
          </Heading3>
          <Body color={{ group: 'tertiary' }}>
            {text.cannotSeeWhileImpersonating}
          </Body>
          <StatusLabel
            status={hasMfa ? 'active' : 'inactive'}
            label={hasMfa ? text.userHasMfa : text.userDoesNotHaveMfa}
          />
        </JustificationContainer>
      </AnimatedBlock>
    );
  }

  return (
    <ProfileCognitoPhoneProvider>
      <MFASettingsBlockInner {...props} />
    </ProfileCognitoPhoneProvider>
  );
};

export default MFASettingsBlock;
